import {Suggestion} from '../models/chat-component.model';

/**
 * Format chat text with support for:
 * - Markdown images
 * - Consecutive images in a row
 * - Clickable actions
 * - Stars
 * - Headings (###, ####)
 * - Bold text (**text**)
 * - Lists (ul, ol, nested)
 * - Horizontal rules
 * - Line breaks
 */
export function formatChatText(text: string): string {
  if (!text) {
    return '';
  }
  // Replace Markdown images with img tags
  text = text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, url) => {
    return `<img class="chat-img-thumb" src="${url}" alt="${alt}" />`;
  });

  // Wrap consecutive images in a flex container
  text = text.replace(/(<img[^>]+>\s*){2,}/g, (match) => {
    return `<div class="chat-img-row">${match}</div>`;
  });

  // Convert @@action|label|value|type@@ → clickable span
  text = text.replace(/@@action\|([^|]+)\|([^|]+)\|([^@]+)@@/g,
    `<span class="chat-action" data-type="$3" data-value="$2">$1</span>`
  );

  // Convert stars like ⭐⭐⭐⭐⭐ (leave as-is, just wrap)
  text = text.replace(/(⭐{1,5})/g, '<span class="stars">$1</span>');

  // Headings ### and ####
  text = text.replace(/^### (.*)$/gm, '<h3>$1</h3>');
  text = text.replace(/^#### (.*)$/gm, '<h4>$1</h4>');

  // Bold text: **text**
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Lists - item (indented or not)
  text = text.replace(/^\s*-\s+(.*)$/gm, '<li>$1</li>');
  text = text.replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');

  // Numbered lists #### 1.
  text = text.replace(/^####\s*\d+\.\s*(.*)$/gm, '<h4>$1</h4>');
  text = text.replace(/^\d+\.\s+(.*)$/gm, '<ol><li>$1</li></ol>');

  // Horizontal rule ---
  text = text.replace(/---/g, '<hr>');

  // Preserve indentation (Room Options with nested lists)
  text = text.replace(/^\s{4,}-\s+(.*)$/gm, '<li class="nested">$1</li>');
  text = text.replace(/(<li class="nested">.*<\/li>)/gs, '<ul class="nested">$1</ul>');

  // Line breaks → <br> (only if not inside <ul>/<ol>)
  text = text.replace(/\n(?!<\/?(ul|ol|li|h3|h4|hr|strong))/g, '<br>');

  return text;
}

/**
 * Filter suggestions to remove empty messages
 */
export function getValidSuggestions(suggestions: Suggestion[]): Suggestion[] {
  return suggestions.filter(s => s.message && s.message.trim() !== '');
}
